import * as React from "react";
import { useState, useEffect } from "react";
import {
  makeStyles,
  tokens,
  Button,
  Card,
  CardHeader,
  CardFooter,
  Spinner,
  Text
} from "@fluentui/react-components";
import { ArrowSync24Regular, Open24Regular } from "@fluentui/react-icons";
import { fetchExcelRange, ExcelRangeData } from "../../services/excel";
import { SpreadsheetRenderer } from "./SpreadsheetRenderer";
import { updateNote } from "../../services/db";
import { isSafeWebUrl } from "../../services/powerpoint";

// ─── Session-level in-memory cache ────────────────────────────────────────────
// Persists for the lifetime of the add-in session (survives component
// unmount/remount). Keyed by noteId. Avoids Graph API round-trips when the
// user navigates away and back to a note they already loaded.
interface CacheEntry {
  data: ExcelRangeData;
  syncedAt: number;
}
const _sessionCache = new Map<string, CacheEntry>();

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** Parse the Firestore content field — handles both old (raw) and new ({data, syncedAt}) format. */
export function parseExcelContent(raw: string | undefined): CacheEntry | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // New format: { data: ExcelRangeData, syncedAt: number }
    if (parsed?.data?.values) {
      return { data: parsed.data, syncedAt: parsed.syncedAt ?? 0 };
    }
    // Legacy format: raw ExcelRangeData
    if (parsed?.values) {
      return { data: parsed, syncedAt: 0 };
    }
    return null;
  } catch {
    return null;
  }
}

interface ExcelLiveNoteProps {
  noteId: string;
  driveItemId: string;
  worksheetName: string;
  rangeAddress: string;
  initialData?: string; // JSON stringified cache from Firestore
  graphToken: string | null;
  noteLabel: string;
  webUrl?: string; // OneDrive link to open the workbook in Excel (parity with PPT)
}

const useStyles = makeStyles({
  card: { marginBottom: "12px", width: "100%" },
  headerAction: { display: "flex", alignItems: "center", gap: "8px" },
  content: { padding: "0 12px 12px 12px" },
  errorText: { color: tokens.colorPaletteRedForeground1, fontSize: "12px" },
  meta: { color: tokens.colorNeutralForeground4, fontSize: "10px", marginTop: "6px" },
});

// Skeleton row widths — gives the illusion of a table loading
const SKELETON_COL_WIDTHS = ['30%', '18%', '18%', '16%', '14%'];
const SKELETON_ROWS = 5;

export const ExcelLiveNote: React.FC<ExcelLiveNoteProps> = ({
  noteId,
  driveItemId,
  worksheetName,
  rangeAddress,
  initialData,
  graphToken,
  noteLabel,
  webUrl,
}) => {
  const styles = useStyles();

  // Determine whether we already have data BEFORE the first render so we
  // can set the correct initial loading state without a blank-frame flash.
  const hasCacheOnMount =
    _sessionCache.has(noteId) || !!parseExcelContent(initialData);

  const [entry, setEntry] = useState<CacheEntry | null>(() => {
    // Populate synchronously from session cache to avoid any blank frame
    return _sessionCache.get(noteId) ?? null;
  });
  const [loading, setLoading] = useState(!hasCacheOnMount);
  const [error, setError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [staleHint, setStaleHint] = useState(false);

  useEffect(() => {
    // Already resolved from session cache in initial state — nothing to do
    if (_sessionCache.has(noteId) && entry) return;

    // Fast: Firestore-persisted cache (JSON parse, no network)
    const fromFirestore = parseExcelContent(initialData);
    if (fromFirestore) {
      _sessionCache.set(noteId, fromFirestore);
      setEntry(fromFirestore);
      setLoading(false);
      return;
    }

    // Slow path: no cache anywhere — fetch from Graph API.
    // loading is already true from initial state so the skeleton shows.
    handleSync();
  }, [noteId, initialData]);

  const handleSync = async () => {
    if (!graphToken) {
      setError("OneDrive not connected. Click 'Enable OneDrive' to sync this note.");
      return;
    }
    if (!driveItemId) {
      setError("No Excel file linked to this note.");
      return;
    }

    setLoading(true);
    setError(null);
    setStaleHint(false);

    // ── Why we poll ──────────────────────────────────────────────────────────
    // Graph can only read what Excel has SAVED to OneDrive. An edit sitting in an
    // open Excel session (esp. desktop Excel without AutoSave) is invisible to a
    // read until it flushes — which can take anywhere from ~1s (Excel Online) to
    // 20s+ (desktop). So a single read right after editing returns the OLD value.
    // We poll with backoff for up to ~30s, updating the table the instant fresh
    // data lands. If it never changes, we surface an actionable "save in Excel"
    // hint instead of silently showing stale data.
    const baselineText = entry?.data?.text ? JSON.stringify(entry.data.text) : null;
    // Cumulative ≈ 0,1.5,3,5,7,10,13,17,22,27s  → ~10 reads over ~27s.
    const BACKOFF_MS = [1500, 1500, 2000, 2000, 3000, 3000, 4000, 5000, 5000];
    const startedAt = Date.now();

    const attemptFetch = async (attempt: number): Promise<void> => {
      try {
        const freshData = await fetchExcelRange(graphToken, driveItemId, worksheetName, rangeAddress);
        const freshText = JSON.stringify(freshData.text);
        const newEntry: CacheEntry = { data: freshData, syncedAt: Date.now() };

        _sessionCache.set(noteId, newEntry);
        setEntry(newEntry); // always show the latest read

        const changed = baselineText === null || freshText !== baselineText;
        const lastAttempt = attempt > BACKOFF_MS.length;

        if (changed) {
          // Got fresh values — persist to Firestore and finish.
          await updateNote(noteId, {
            content: JSON.stringify({ data: freshData, syncedAt: newEntry.syncedAt }),
            attachmentType: "excel-live-link",
          });
          setSyncMsg(null);
          setSyncSuccess(true);
          setTimeout(() => setSyncSuccess(false), 3000);
          setLoading(false);
        } else if (lastAttempt) {
          // Waited the full window and OneDrive still reports the same values.
          // Most likely the edit hasn't been saved out of Excel yet.
          setSyncMsg(null);
          setStaleHint(true);
          setLoading(false);
        } else {
          // Unchanged — the OneDrive save may still be in flight; wait and read again.
          const elapsed = Math.round((Date.now() - startedAt) / 1000);
          setSyncMsg(`Waiting for OneDrive to save your Excel changes… ${elapsed}s`);
          setTimeout(() => { attemptFetch(attempt + 1); }, BACKOFF_MS[attempt - 1]);
        }
      } catch (err: any) {
        console.error("Excel Sync Error:", err);
        setSyncMsg(null);
        const raw = String(err?.message || "");
        const isAuthErr = /IDX14100|not well formed|JWT|InvalidAuthenticationToken|CompactToken|80049217|401|Access token|expired|Unauthorized/i.test(raw);
        if (isAuthErr) {
          // The note modal is itself an Office dialog, and Office allows only ONE
          // dialog at a time — so we can't refresh the token from in here. The user
          // must reconnect from the task pane.
          setError(
            "Your OneDrive connection has expired. Close this note, then in the MarginPro " +
            "task pane reconnect OneDrive (Settings → OneDrive / “Enable OneDrive”), and reopen the note to refresh."
          );
        } else {
          setError(raw || "Failed to fetch Excel data. Check your OneDrive connection.");
        }
        setLoading(false);
      }
    };

    attemptFetch(1);
  };

  const syncedLabel = entry?.syncedAt
    ? `Synced ${new Date(entry.syncedAt).toLocaleString()}`
    : "";

  return (
    <Card className={styles.card}>
      <CardHeader
        header={<Text weight="semibold">{noteLabel}</Text>}
        description={
          <Text size={200}>
            {worksheetName}
            {rangeAddress && rangeAddress !== "auto" ? `!${rangeAddress}` : " (full range)"}
          </Text>
        }
        action={
          <div className={styles.headerAction}>
            {syncSuccess && (
              <Text size={200} style={{ color: tokens.colorPaletteGreenForeground1, whiteSpace: "nowrap" }}>
                ✅ Updated
              </Text>
            )}
            {loading && <Spinner size="extra-tiny" />}
            <Button
              appearance="subtle"
              icon={<ArrowSync24Regular />}
              onClick={(e) => { e.stopPropagation(); handleSync(); }}
              disabled={loading}
              title="Refresh from OneDrive"
            />
          </div>
        }
      />

      <div className={styles.content}>
        {isSafeWebUrl(webUrl) && (
          <div style={{ marginBottom: "8px" }}>
            <Button
              appearance="primary"
              icon={<Open24Regular />}
              style={{ backgroundColor: "#2f8570" }}
              onClick={(e) => { e.stopPropagation(); window.open(webUrl!, "_blank", "noopener,noreferrer"); }}
            >
              Open in Excel
            </Button>
          </div>
        )}

        {error && <div className={styles.errorText}>{error}</div>}

        {syncMsg && (
          <div style={{ color: tokens.colorNeutralForeground3, fontSize: "11px", marginBottom: "6px" }}>
            {syncMsg}
          </div>
        )}

        {staleHint && (
          <div style={{
            color: tokens.colorPaletteDarkOrangeForeground1,
            backgroundColor: tokens.colorPaletteDarkOrangeBackground1,
            border: `1px solid ${tokens.colorPaletteDarkOrangeBorderActive}`,
            borderRadius: "4px",
            padding: "8px 10px",
            fontSize: "11px",
            marginBottom: "8px",
            lineHeight: 1.4,
          }}>
            Showing the last values OneDrive has saved. If you just edited this workbook in the
            <strong> Excel desktop app</strong>, save it (<strong>Ctrl/Cmd&nbsp;+&nbsp;S</strong>) or turn on
            <strong> AutoSave</strong> so the changes reach OneDrive — then hit refresh again.
            Edits made in <strong>Excel for the web</strong> usually sync within a few seconds.
          </div>
        )}

        {entry?.data ? (
          // ── Data loaded — show the spreadsheet ──────────────────────────────
          <>
            <SpreadsheetRenderer data={entry.data} />
            {syncedLabel && <div className={styles.meta}>{syncedLabel}</div>}
          </>
        ) : loading ? (
          // ── Loading skeleton — card is visible immediately while fetching ──
          <div style={{ paddingTop: "4px" }}>
            {/* Fake header row */}
            <div style={{
              display: "flex", gap: "4px", marginBottom: "5px"
            }}>
              {SKELETON_COL_WIDTHS.map((w, i) => (
                <div key={i} style={{
                  height: "20px",
                  width: w,
                  backgroundColor: "#2f857055",
                  borderRadius: "3px",
                }} />
              ))}
            </div>
            {/* Fake data rows */}
            {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
              <div key={rowIdx} style={{
                display: "flex", gap: "4px", marginBottom: "3px"
              }}>
                {SKELETON_COL_WIDTHS.map((w, colIdx) => (
                  <div key={colIdx} style={{
                    height: "16px",
                    width: w,
                    backgroundColor: rowIdx % 2 === 0 ? "#e5e7eb" : "#f3f4f6",
                    borderRadius: "2px",
                    opacity: 1 - rowIdx * 0.08,
                  }} />
                ))}
              </div>
            ))}
            <Text
              size={100}
              style={{ color: tokens.colorNeutralForeground4, display: "block", marginTop: "10px" }}
            >
              Loading from OneDrive…
            </Text>
          </div>
        ) : (
          // ── No cache, not loading, no error ─────────────────────────────────
          !error && (
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              No data cached yet. Click the sync button to load from OneDrive.
            </Text>
          )
        )}
      </div>
    </Card>
  );
};
